import { categories } from '../data/products'

// Shared truckload-split helpers — used by the Hero estimator, the Services
// hub, the Product page, and anywhere else we add multi-load orders to the
// cart. Centralizing keeps the per-load minimum billing rules and the
// multi-delivery line-item split consistent across every entry point.
//
// 50/50 Mix is classified HEAVY — even though the name reads "mix" with
// topsoil, the blend is dense enough (sand + topsoil) that it ships in the
// same truckload class as rock and aggregates.
export function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('topsoil') ||
    n.includes('play chips') ||
    // Hog fuel is ground wood — ~800 lbs/yd, the lightest thing we haul, so
    // it rides in the largest light truckload alongside bark and compost.
    n.includes('hog fuel')
  )
}

/**
 * Legal payload for a solo dump truck, in pounds. 15.5 tons is the binding
 * constraint on every delivery — a truck runs out of weight long before it
 * runs out of box on anything denser than bark.
 */
export const MAX_PAYLOAD_LBS = 31000

/**
 * Standard truckloads by material class. Light organics (bark, compost,
 * topsoil, play chips, hog fuel) ride 15 yd; everything else ships 12 yd.
 * These are the operational load sizes we quote and dispatch against.
 */
const LIGHT_LOAD_YARDS = 15
const STANDARD_LOAD_YARDS = 12

/**
 * Large rock and erosion-control stone is the one class where 12 yards is
 * genuinely over legal weight — at ~1.5–1.6 tons/yd a 12 yd load scales
 * 18–19 tons against a 15.5-ton payload. These materials cap on WEIGHT
 * instead, which lands them around 9–10 yards per load.
 */
export function isWeightCappedMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('spalls') ||
    n.includes('rip rap') ||
    n.includes('rip-rap') ||
    n.includes('large rocks')
  )
}

// Density lookup, keyed by lowercased product name, parsed once from the
// catalog's published spec. The spec string is the single source of truth for
// how heavy a yard is — it drives the tons-to-yards chart, the spec sheet, and
// now the load cap, so those three can never disagree.
const densityByName: Record<string, number> = (() => {
  const map: Record<string, number> = {}
  for (const category of categories) {
    for (const product of category.products) {
      const raw = product.specs?.weightPerYard
      if (!raw) continue
      const match = raw.replace(/,/g, '').match(/(\d+(?:\.\d+)?)\s*lbs/i)
      if (match) map[product.name.toLowerCase()] = Number(match[1])
    }
  }
  return map
})()

/**
 * Pounds per cubic yard for a product. Cart line items carry a
 * "(Delivery 2)" suffix, so the name is normalized before lookup. Anything
 * unrecognized falls back to a deliberately HEAVY assumption — under-guessing
 * density would overload the truck and under-bill the material, so the safe
 * error is to assume the yard is heavy.
 */
export function getLbsPerYard(name: string): number {
  const base = name
    .toLowerCase()
    .replace(/\s*\(delivery\s*\d+\)\s*$/, '')
    .trim()
  const known = densityByName[base]
  if (known) return known
  return isLightMaterial(name) ? 1200 : 3000
}

/**
 * Yards per truckload for a material.
 *
 * Light organics: 15 yd. Everything else: 12 yd. The exception is large rock
 * and erosion-control stone, which is dense enough that 12 yd exceeds the
 * 15.5-ton payload — those cap on weight instead.
 */
export function getFullLoad(name: string): number {
  if (isWeightCappedMaterial(name)) {
    const byWeight = Math.floor(MAX_PAYLOAD_LBS / getLbsPerYard(name))
    return Math.max(1, Math.min(STANDARD_LOAD_YARDS, byWeight))
  }
  return isLightMaterial(name) ? LIGHT_LOAD_YARDS : STANDARD_LOAD_YARDS
}

/**
 * Gravel proper: crushed minus, crushed clear, drain rock, pit run, gravel
 * borrow, recycled aggregate, and pea gravel. These all convert at the flat
 * 1.3 tons/yd we quote and buy against.
 *
 * Deliberately excludes sand, fill dirt, decorative stone (river rock,
 * cobble, lava rock), ballast, and large quarry rock — those are genuinely
 * lighter or heavier than 1.3, and forcing them onto one factor would put a
 * wrong weight on their product pages.
 */
export function isGravelMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('minus') ||
    n.includes('crushed clear') ||
    n.includes('drain rock') ||
    n.includes('pit run') ||
    n.includes('gravel borrow') ||
    n.includes('walkway gravel') ||
    n.includes('pea gravel') ||
    n.includes('recycled')
  )
}

/**
 * Tons per cubic yard, used wherever we state what a delivery weighs.
 *
 * Gravel converts at the flat 1.3 tons/yd above. Everything else uses its own
 * published catalog density, so bark stays light and quarry rock stays heavy.
 */
export function getTonsPerYard(name: string): number {
  if (isGravelMaterial(name)) return 1.3
  return getLbsPerYard(name) / 2000
}

/**
 * Load weight in tons for a given yardage — used wherever we state what a
 * delivery actually weighs.
 */
export function getLoadTons(yards: number, name: string): number {
  return yards * getTonsPerYard(name)
}

// The default yardage we pre-fill in quote forms and the product detail
// page's quantity selector: one full truckload of whatever they're looking at
// — 15 yd for light organics, 12 yd standard, less for large rock.
export function getDefaultQuantity(productName: string): string {
  if (!productName) return String(STANDARD_LOAD_YARDS)
  return String(getFullLoad(productName))
}

// Minimum billable yards for a STANDALONE order (single delivery). We accept
// orders as small as 3 yd so homeowners with planter beds, paver bases, and
// small drainage repairs can use us. Anything smaller is bumped up to the
// 3 yd order minimum — trucks have fixed dispatch costs and 1–2 yd orders
// don't cover them. Partial last loads in a multi-delivery split are NOT
// bumped — if a customer is already paying for at least one full load, the
// partial is billed at its actual yardage (with the small-load surcharge
// applied per `getSmallLoadSurcharge` below).
// Small-load surcharge threshold per material type. Below this many yards
// in a single load, we add a surcharge to compensate for fixed dispatch
// cost. Threshold is 10 yd for ALL materials — once a customer is at or
// above 10 yd, they get base price whether the truck is light- or
// heavy-loaded. Light materials still ride in larger 15 yd loads and
// still qualify for the 5%-at-15-yd bulk discount via CartContext.
export function getSmallLoadThreshold(_productName: string): number {
  return 10
}

/**
 * Compute the delivery fee for a specific load.
 * Loads under 3 yards get a flat $250 delivery fee to cover dispatch costs.
 * Loads 3 yards and up get the standard zone delivery fee.
 */
export function getLoadDeliveryFee(yards: number, zoneFee: number): number {
  if (yards > 0 && yards < 3) return 250
  return zoneFee
}

/**
 * Small-load markup schedule. Below 10 yd a truck still costs the same to
 * dispatch, load, and drive, so the per-yard price carries a markup that
 * tapers off as the load approaches a full truck.
 *
 * The curve is deliberately steep at the bottom and shallow at the top: it
 * flattens hard between 8 and 10 yd so a customer sitting at 8 or 9 yd has a
 * visible, cheap reason to round up to 10 and hit base price.
 *
 * [minimum yards for this tier, markup over base price]
 */
export const SMALL_LOAD_MARKUP_TIERS: readonly [number, number][] = [
  [10, 0], // 10+ yd — base price
  [9, 0.08],
  [8, 0.14],
  [7, 0.2],
  [5, 0.3], // 5–6 yd
  [3, 0.4], // 3–4 yd
]

/**
 * Compute the small-load price surcharge multiplier for a given yardage.
 *
 * - At or above 10 yd: 1.0× (base price)
 * - 3–9 yd: tiered markup per SMALL_LOAD_MARKUP_TIERS
 * - Under 3 yd: 1.0× (no markup, because the $250 flat delivery fee covers dispatch)
 */
export function getSmallLoadSurcharge(
  yards: number,
  productName: string,
): number {
  if (yards < 3) return 1 // Flat $250 delivery fee handles the economics for < 3yd
  const threshold = getSmallLoadThreshold(productName)
  if (yards >= threshold) return 1
  const tier = SMALL_LOAD_MARKUP_TIERS.find(([min]) => yards >= min)
  return 1 + (tier ? tier[1] : 0)
}

/**
 * Split a total yardage into per-delivery quantities.
 *
 * Rules:
 * - Loads 1..N-1 are always fullLoad yards.
 * - The last load is the actual remainder.
 *
 * Returns an array of yards per delivery. e.g. for 5/8" Minus (12 yd/load):
 *   splitIntoDeliveries(16) → [12, 4]
 *   splitIntoDeliveries(24) → [12, 12]
 *   splitIntoDeliveries(9)  → [9]
 *   splitIntoDeliveries(2)  → [2]
 */
export function splitIntoDeliveries(
  totalYards: number,
  productName: string,
): number[] {
  const fullLoad = getFullLoad(productName)
  const total = Math.max(0, Math.round(totalYards))
  if (total <= 0) return []
  if (total <= fullLoad) {
    return [total]
  }
  const deliveries = Math.ceil(total / fullLoad)
  const lastLoad = total - (deliveries - 1) * fullLoad
  const loads: number[] = []
  for (let i = 0; i < deliveries - 1; i++) loads.push(fullLoad)
  loads.push(lastLoad)
  return loads
}

/**
 * Convenience: turn a split result into cart-line-item descriptors so callers
 * can iterate and addToCart() consistently. The product name gets a
 * "(Delivery N)" suffix when there's more than one load, matching the cart
 * drawer's per-delivery breakdown rendering.
 */
export function buildDeliveryLineItems(
  totalYards: number,
  productName: string,
): { product: string; quantity: number }[] {
  const loads = splitIntoDeliveries(totalYards, productName)
  if (loads.length === 0) return []
  if (loads.length === 1) {
    return [
      {
        product: productName,
        quantity: loads[0],
      },
    ]
  }
  return loads.map((qty, i) => ({
    product: `${productName} (Delivery ${i + 1})`,
    quantity: qty,
  }))
}
